import { capitalize, groupBy, isEmpty } from 'lodash';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Controller, UseFormProps, UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, Form, useFormContext } from '@nicoknoll/forms';
import { classnames } from '@nicoknoll/utils';

import {
    DEFAULT_LANGUAGE_CODE,
    SUPPORTED_LANGUAGE_CODES,
    SupportedLanguageCode,
    TranslatableSuggestion,
    WithTranslations,
} from '../../../data/models.ts';

const createTranslatableFieldValues = <T extends Record<string, any>, K extends keyof T>(
    obj: T,
    translatableKeys: readonly K[]
): WithTranslations<T, K> => {
    const result = { ...obj } as any;
    const languageCodes = SUPPORTED_LANGUAGE_CODES.filter((lang) => lang !== DEFAULT_LANGUAGE_CODE);

    translatableKeys.forEach((key) => {
        languageCodes.forEach((lang) => {
            const capitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
            const translatedKey = `${String(key)}${capitalizedLang}`;
            result[translatedKey] = obj[key];
        });
    });

    return result;
};

const TranslatableFormField = ({
    name,
    children,
    rules,
    multiple = false,
    ...props
}: React.ComponentPropsWithRef<typeof Form.Field> & { multiple?: boolean }) => {
    const id = useId();
    const { t } = useTranslation('common');
    const { formState, control, setValue, getValues } = useFormContext();

    const [locale, setLocale] = useState<SupportedLanguageCode>(DEFAULT_LANGUAGE_CODE);

    useEffect(() => {
        if (formState.isSubmitting) {
            setLocale(DEFAULT_LANGUAGE_CODE);
        }
    }, [formState.isSubmitting]);

    if (!React.isValidElement(children)) {
        throw new Error('TranslatableFormField children must be a valid React element');
    }

    const { label, ...fieldProps } = children.props as any;

    return (
        <div className="flex flex-col">
            <div className="flex flex-col ">
                <Field.Label required={fieldProps.required} htmlFor={fieldProps.id || id}>
                    {label}
                </Field.Label>

                <div className="flex gap-0.5 items-center pt-1 pb-1.5 rounded-md">
                    {SUPPORTED_LANGUAGE_CODES.map((key) => (
                        <button
                            key={key}
                            onClick={() => setLocale(key)}
                            className={classnames(
                                'text-base-400 font-medium text-xs px-1.5 py-0.5 rounded border border-transparent hover:bg-base-50 hover:text-base-500 transition-colors',
                                key === locale && '!text-theme-700 !bg-theme-50'
                            )}
                            type="button"
                        >
                            {t(`languageCode.${key}`)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                {SUPPORTED_LANGUAGE_CODES.map((key) => {
                    const fieldName = `${name}${key !== DEFAULT_LANGUAGE_CODE ? capitalize(key) : ''}`;
                    return (
                        <div className={classnames(key === locale ? 'flex gap-1' : 'hidden')} key={key}>
                            <Controller
                                key={key}
                                name={fieldName}
                                control={control}
                                rules={{
                                    ...(rules || {}),
                                    required: key === DEFAULT_LANGUAGE_CODE ? rules?.required : undefined,
                                }}
                                render={({ field: { onChange, ...field }, fieldState }) => {
                                    const resetFieldsExceptCustomValues = () => {
                                        const fieldValue = getValues(fieldName);

                                        for (const lang of SUPPORTED_LANGUAGE_CODES) {
                                            if (lang !== key) {
                                                const fieldName = `${name}${lang !== DEFAULT_LANGUAGE_CODE ? capitalize(lang) : ''}`;
                                                const valueKey: keyof TranslatableSuggestion = `value${capitalize(lang)}`;
                                                const isCustomValue = !fieldProps.options.some(
                                                    (option: TranslatableSuggestion) => {
                                                        if (multiple) {
                                                            return fieldValue?.includes(option[valueKey]);
                                                        } else {
                                                            return option[valueKey] === fieldValue;
                                                        }
                                                    }
                                                );

                                                if (!isCustomValue) {
                                                    setValue(fieldName, multiple ? [] : '');
                                                }
                                            }
                                        }
                                    };

                                    const handleChange = (event: { target: any }) => {
                                        const eventValue = multiple
                                            ? Array.from(event.target.selectedOptions, (option: any) => option.value)
                                            : event.target.value;

                                        // change is only triggered when a value is selected from the list
                                        if (fieldProps.options) {
                                            // in case we have a field with options but also the option to add custom ones
                                            // lets see if the selected one is in the list
                                            if (event.target.value) {
                                                const valueKey: keyof TranslatableSuggestion = `value${capitalize(key)}`;

                                                // in case it is from the list lets set the other language values to the selected one
                                                // fieldName for all except the changed language
                                                const selectedOption = multiple
                                                    ? fieldProps.options.filter((option: TranslatableSuggestion) => {
                                                          return eventValue?.includes(option[valueKey]);
                                                      })
                                                    : fieldProps.options.find((option: TranslatableSuggestion) => {
                                                          return option[valueKey] === eventValue;
                                                      });

                                                // update the fields except the one that was changed
                                                for (const lang of SUPPORTED_LANGUAGE_CODES) {
                                                    if (lang !== key) {
                                                        const fieldName = `${name}${lang !== DEFAULT_LANGUAGE_CODE ? capitalize(lang) : ''}`;
                                                        const valueKey: keyof TranslatableSuggestion = `value${capitalize(lang)}`;
                                                        setValue(
                                                            fieldName,
                                                            multiple
                                                                ? selectedOption?.map((o: any) => o[valueKey])
                                                                : selectedOption?.[valueKey]
                                                        );
                                                    }
                                                }
                                            } else {
                                                // if the current field is empty, clear all other fields except if they have custom values
                                                resetFieldsExceptCustomValues();
                                            }
                                        }

                                        return onChange({ ...event, target: { ...event.target, value: eventValue } });
                                    };

                                    const handleAddOption = (value: string) => {
                                        resetFieldsExceptCustomValues();

                                        // add option doesn't have access to the new field name, so we need to set it here
                                        setValue(fieldName, value);

                                        // return fieldProps.onAddOption?.(value);
                                    };

                                    let translatedOptions = fieldProps.options?.map(
                                        (option: TranslatableSuggestion) => {
                                            const groupNameKey: keyof TranslatableSuggestion = `groupName${capitalize(key)}`;
                                            const valueKey: keyof TranslatableSuggestion = `value${capitalize(key)}`;
                                            return {
                                                ...option,
                                                groupName: option[groupNameKey],
                                                label: option[valueKey],
                                                value: option[valueKey],
                                            };
                                        }
                                    );

                                    if (translatedOptions?.some((option: TranslatableSuggestion) => option.groupName)) {
                                        translatedOptions = Object.entries(groupBy(translatedOptions, 'groupName'));
                                    }

                                    return React.cloneElement(children, {
                                        ...field,
                                        ...fieldProps,
                                        options: translatedOptions,
                                        label: '',
                                        onChange: handleChange,
                                        onAddOption: handleAddOption,
                                        error: fieldState.error,
                                        required: key === DEFAULT_LANGUAGE_CODE ? fieldProps.required : undefined,
                                        id: fieldProps.id || id,
                                        className: classnames(fieldProps.className, 'flex-1'),
                                        placeholder: fieldProps.placeholder != null ? fieldProps.placeholder : label,
                                    });
                                }}
                            />

                            <div className="leading-8"></div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const pickTranslatableFormValues = <T extends Record<string, any>>(
    defaultValues: T,
    data: any,
    translatableFieldNames: readonly string[] = []
): T => {
    const translatableDefaultValues = createTranslatableFieldValues(defaultValues, translatableFieldNames);
    const result = { ...translatableDefaultValues } as T;
    if (!data || isEmpty(data)) return result;

    (Object.keys(translatableDefaultValues) as Array<keyof T>).forEach((key) => {
        if (translatableFieldNames.includes(key as string)) {
            // fill default field with default language value e.g. fill name field with nameDe value
            const capitalizedLang = DEFAULT_LANGUAGE_CODE.charAt(0).toUpperCase() + DEFAULT_LANGUAGE_CODE.slice(1);
            const sourceKey = `${String(key)}${capitalizedLang}`;
            if (data[sourceKey] != null) {
                result[key] = data[sourceKey]!;
            }
        } else {
            if (data[key] != null) {
                result[key] = data[key]!;
            }
        }
    });

    return result;
};

export const useTranslatableForm = <TFieldValues extends Record<string, any> = Record<string, any>, TContext = any>({
    defaultValues,
    translatableFieldNames = [],
    data,
    ...props
}: UseFormProps<TFieldValues, TContext> & {
    defaultValues: TFieldValues;
    translatableFieldNames?: readonly string[];
    data?: object | null;
}): UseFormReturn<TFieldValues, TContext> & { resetFromData: (data: object) => void } => {
    // put default language value in default field (without language code) and language specific ones in fields with lang suffix
    const formMethods = useForm<TFieldValues, TContext>({
        mode: 'onTouched',
        ...props,
        defaultValues: pickTranslatableFormValues(defaultValues, data, translatableFieldNames),
    });
    const previousData = useRef(data);
    const {
        reset,
        formState: { dirtyFields },
    } = formMethods;

    useEffect(() => {
        if (previousData.current === data) return;

        const previousId = previousData.current && 'id' in previousData.current ? previousData.current.id : undefined;
        const currentId = data && 'id' in data ? data.id : undefined;
        const isSameRecord = previousId === currentId;
        previousData.current = data;

        reset(
            pickTranslatableFormValues(defaultValues, data, translatableFieldNames),
            isSameRecord ? { keepDirtyValues: true, keepDirty: true, keepErrors: true, keepTouched: true } : undefined
        );
        // Reading dirtyFields subscribes the form to the state keepDirtyValues needs.
        // Inline field configuration may change identity without new server data.
    }, [data, defaultValues, translatableFieldNames, reset, dirtyFields]);

    return {
        ...formMethods,
        resetFromData: (savedData: object) =>
            reset(pickTranslatableFormValues(defaultValues, savedData, translatableFieldNames)),
    };
};

export default TranslatableFormField;
